import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const BASE_API = process.env.REACT_APP_BASE_API;
const KRANKQUE_SECRET_KEY = process.env.REACT_APP_KRANKQUE_SECRET_KEY;
const KRANKQUE_APPLICATION_ID = process.env.REACT_APP_KRANKQUE_APPLICATION_ID;

interface VerifyPhoneInterface {
  countryCode: string;
  phone: string;
  skipAdmin?: boolean;
  isAndroid?: boolean;
  skipAll?: boolean;
}

interface VerifyCodeInterface {
  countryCode: string;
  phone: string;
  otp: string;
  skipAdmin?: boolean;
  skipAll?: boolean;
}

interface SignUpInterface {
  email: string;
  password: string;
  accountType: string;
}

interface LoginInterface {
  email: string;
  password: string;
}

interface LogoutInterface {
  body: {
    userId: string;
  };
  accessToken: string;
}

interface VerifyRequestInterface {
  body: VerifyCodeInterface | VerifyPhoneInterface;
  accessToken: string;
}

interface GetMeInterface {
  accessToken: string;
}
interface GoogleAuth {
  code: string;
  accountType?: string;
}

interface AppleAuth {
  idToken: string;
  email: string;
  firstName: string;
  lastName: string;
}

interface UpdateProfileBody {
  email?: string;
  password?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  accountType?: string;
  company?: any;
  authType?: string;
  avatarKey?: string;
  description?: string;
}

interface UpdateProfile {
  body: UpdateProfileBody;
  id: string;
  accessToken: string;
}

interface DeleteProfile {
  id: string;
  accessToken: string;
}

interface GetCompanyEmployeesInterface {
  id: string;
  limit: number;
  skip: number;
  searchQuery: string;
  accessToken: string;
}

interface GetCompanyRequestsInterface {
  id: string;
  limit: number;
  skip: number;
  searchQuery: string;
  accessToken: string;
}

interface UpdateRequestsInterface {
  accessToken: string;
  requestId: string;
  status: string;
}

interface RemoveMemberInterface {
  accessToken: string;
  userId: string;
  companyId: string;
}

interface RequestChangeEmailInterface {
  accessToken: string;
  email: string;
}

interface ConfirmChangeEmailInterface {
  email: any;
  accessToken: string;
  token: string;
}
interface ChangePasswordInterface {
  email: string;
  newPassword: string;
  oldPassword?: string;
}
export const userSlice = createApi({
  reducerPath: 'user',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_API,
    prepareHeaders: (headers, { getState }) => {
      headers.set('Content-Type', 'application/json');

      if (KRANKQUE_SECRET_KEY) {
        headers.set('krankque-secret-key', KRANKQUE_SECRET_KEY);
      }

      if (KRANKQUE_APPLICATION_ID) {
        headers.set('krankque-application-id', KRANKQUE_APPLICATION_ID);
      }

      return headers;
    }
  }),
  tagTypes: ['User', 'Members'],
  endpoints: (builder) => ({
    verifyPhone: builder.mutation<any, VerifyRequestInterface>({
      query: (req) => ({
        url: '/verify-phone',
        method: 'POST',
        body: req.body,
        headers: {
          Authorization: `Bearer ${req.accessToken}`
        }
      })
    }),
    verifyCode: builder.mutation<any, VerifyRequestInterface>({
      query: (req) => ({
        url: '/verify-otp',
        method: 'POST',
        body: req.body,
        headers: {
          Authorization: `Bearer ${req.accessToken}`
        }
      })
    }),
    signUp: builder.mutation<any, SignUpInterface>({
      query: (body) => ({
        url: '/sign-up',
        method: 'POST',
        body
      })
    }),
    login: builder.mutation<any, LoginInterface>({
      query: (body) => ({
        url: '/sign-in',
        method: 'POST',
        body
      })
    }),
    logout: builder.mutation<any, LogoutInterface>({
      query: (req) => ({
        url: '/logout',
        method: 'POST',
        body: req.body,
        headers: {
          Authorization: `Bearer ${req.accessToken}`
        }
      })
    }),
    getMe: builder.query<any, GetMeInterface>({
      query: (req) => ({
        url: '/me',
        method: 'GET',
        headers: {
          Authorization: `Bearer ${req.accessToken}`
        }
      }),
      providesTags: ['User', 'Members']
    }),
    updateProfile: builder.mutation<any, UpdateProfile>({
      query: (req) => ({
        url: `/profile?id=${req.id}`,
        method: 'PUT',
        body: req.body,
        headers: {
          Authorization: `Bearer ${req.accessToken}`
        }
      }),
      invalidatesTags: ['User']
    }),

    deleteProfile: builder.mutation<any, DeleteProfile>({
      query: (req) => ({
        url: `/profile?id=${req.id}`,
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${req.accessToken}`
        }
      }),
      invalidatesTags: ['User']
    }),

    googleAuth: builder.mutation<any, GoogleAuth>({
      query: (req) => ({
        url: `/google-auth`,
        method: 'POST',
        body: req
      }),
      invalidatesTags: ['User']
    }),

    appleAuth: builder.mutation<any, AppleAuth>({
      query: (req) => ({
        url: `/apple-auth`,
        method: 'POST',
        body: req
      }),
      invalidatesTags: ['User']
    }),

    getCompanyEmployees: builder.query<any, GetCompanyEmployeesInterface>({
      query: (req) => ({
        url: `/company/employees?id=${req.id}&limit=${req.limit}&skip=${req.skip}&sort=asc&searchQuery=${req.searchQuery}`,
        headers: {
          Authorization: `Bearer ${req.accessToken}`
        }
      })
    }),

    getCompanyRequests: builder.query<any, GetCompanyRequestsInterface>({
      query: (req) => ({
        url: `/company/requests?id=${req.id}&limit=${req.limit}&skip=${req.skip}&sort=asc&searchQuery=${req.searchQuery}`,
        headers: {
          Authorization: `Bearer ${req.accessToken}`
        }
      })
    }),

    updateRequest: builder.mutation<any, UpdateRequestsInterface>({
      query: (req) => ({
        url: `/company/requests`,
        method: 'PUT',
        body: {
          requestId: req.requestId,
          status: req.status
        },
        headers: {
          Authorization: `Bearer ${req.accessToken}`
        }
      }),
      invalidatesTags: ['Members']
    }),

    removeMember: builder.mutation<any, RemoveMemberInterface>({
      query: (req) => ({
        url: `/company/reject`,
        method: 'POST',
        body: {
          userId: req.userId,
          companyId: req.companyId
        },
        headers: {
          Authorization: `Bearer ${req.accessToken}`
        }
      }),
      invalidatesTags: ['Members']
    }),
    requestChangeEmail: builder.mutation<any, RequestChangeEmailInterface>({
      query: (req) => ({
        url: '/request-change-email',
        method: 'POST',
        body: { email: req.email },
        headers: {
          Authorization: `Bearer ${req.accessToken}`
        }
      })
    }),

    confirmChangeEmail: builder.mutation<any, ConfirmChangeEmailInterface>({
      query: (req) => ({
        url: '/change-email',
        method: 'POST',
        body: { email: req.email },
        headers: {
          Authorization: `Bearer ${req.accessToken}`
        }
      }),
      invalidatesTags: ['User']
    }),
    forgotPassword: builder.mutation<any, { email: string }>({
      query: (body) => ({
        url: '/forgot-password',
        method: 'POST',
        body
      })
    }),
    changePassword: builder.mutation<any, ChangePasswordInterface>({
      query: (body) => ({
        url: '/change-password',
        method: 'POST',
        body
      })
    })
  })
});

export const {
  useVerifyPhoneMutation,
  useVerifyCodeMutation,
  useSignUpMutation,
  useGetMeQuery,
  useUpdateProfileMutation,
  useLoginMutation,
  useLogoutMutation,
  useDeleteProfileMutation,
  useGoogleAuthMutation,
  useGetCompanyEmployeesQuery,
  useGetCompanyRequestsQuery,
  useUpdateRequestMutation,
  useRemoveMemberMutation,
  useAppleAuthMutation,
  useRequestChangeEmailMutation,
  useConfirmChangeEmailMutation,
  useForgotPasswordMutation,
  useChangePasswordMutation,
} = userSlice;
